import React from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Minus, Plus, Trash2, ShoppingBag, Flame, ArrowRight } from "lucide-react";
import { useCart } from "../contexts/CartContext";

const slide = {
    hidden:  { x: "100%" },
    visible: { x: 0,      transition: { type: "spring", stiffness: 220, damping: 28 } },
    exit:    { x: "100%", transition: { duration: 0.35, ease: [0.5, 0, 0.75, 0] } },
};

/**
 * Cinematic slide-in cart drawer (Apple × Awwwards style).
 * Used across the burger brand surfaces.
 */
export const CartDrawer = ({ open, onClose }) => {
    const navigate = useNavigate();
    const { items, subtotal, setQty, remove } = useCart();

    return (
        <AnimatePresence>
            {open && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        data-testid="cart-backdrop"
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        onClick={onClose}
                        className="fixed inset-0 z-[800] bg-black/70 backdrop-blur-md"
                    />
                    {/* Drawer */}
                    <motion.aside
                        data-testid="cart-drawer"
                        variants={slide}
                        initial="hidden" animate="visible" exit="exit"
                        className="fixed top-0 right-0 bottom-0 z-[810] w-full sm:max-w-md burger-bg border-l border-[var(--burger-orange)]/15 flex flex-col"
                    >
                        {/* Header */}
                        <header className="px-6 py-5 border-b border-white/5 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <span className="h-9 w-9 grid place-items-center rounded-xl ember-gradient">
                                    <ShoppingBag size={16} className="text-black" />
                                </span>
                                <div>
                                    <p className="text-[10px] font-display tracking-[0.32em] text-white/45">// YOUR ORDER</p>
                                    <h2 className="font-display text-2xl tracking-[0.18em]">CART</h2>
                                </div>
                            </div>
                            <button
                                onClick={onClose}
                                data-testid="cart-close"
                                className="h-10 w-10 grid place-items-center rounded-full bg-white/5 hover:bg-white/10 transition"
                            >
                                <X size={18} />
                            </button>
                        </header>

                        {/* Items */}
                        <div className="flex-1 overflow-y-auto cinematic-scrollbar px-6 py-4 space-y-3">
                            {items.length === 0 ? (
                                <div data-testid="cart-empty" className="text-center py-20">
                                    <Flame size={42} className="mx-auto text-white/15 mb-3" />
                                    <p className="font-display text-3xl tracking-tight text-white/40">EMPTY CART</p>
                                    <p className="text-xs text-white/50 mt-2">Πρόσθεσε ένα burger για να ξεκινήσεις.</p>
                                </div>
                            ) : (
                                items.map((it, idx) => (
                                    <motion.article
                                        key={it.id + idx}
                                        layout
                                        initial={{ opacity: 0, x: 30 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        exit={{ opacity: 0, x: 30 }}
                                        transition={{ duration: 0.35 }}
                                        data-testid={`cart-item-${it.id}`}
                                        className="flex gap-3 p-3 rounded-2xl glass-burger relative overflow-hidden group"
                                    >
                                        <img src={it.image_url} alt={it.name} className="w-16 h-16 rounded-xl object-cover shrink-0" />
                                        <div className="flex-1 min-w-0">
                                            <h3 className="font-display text-base tracking-wide truncate">{it.name.toUpperCase()}</h3>
                                            <p className="ember-text font-mono text-sm font-bold mt-0.5">€{(it.price * it.qty).toFixed(2)}</p>
                                            <div className="flex items-center gap-2 mt-2">
                                                <button
                                                    onClick={() => setQty(it.id, it.qty - 1)}
                                                    className="h-7 w-7 grid place-items-center rounded-full bg-white/8 hover:bg-white/15 transition"
                                                >
                                                    <Minus size={12} />
                                                </button>
                                                <span className="font-display text-lg w-6 text-center">{it.qty}</span>
                                                <button
                                                    onClick={() => setQty(it.id, it.qty + 1)}
                                                    className="h-7 w-7 grid place-items-center rounded-full ember-gradient text-black transition hover:scale-110"
                                                >
                                                    <Plus size={12} strokeWidth={3} />
                                                </button>
                                                <button
                                                    onClick={() => remove(it.id)}
                                                    className="ml-auto h-7 w-7 grid place-items-center rounded-full hover:bg-red-500/20 text-white/40 hover:text-red-400 transition"
                                                >
                                                    <Trash2 size={13} />
                                                </button>
                                            </div>
                                        </div>
                                    </motion.article>
                                ))
                            )}
                        </div>

                        {/* Footer */}
                        {items.length > 0 && (
                            <footer className="px-6 py-5 border-t border-white/5 space-y-3">
                                <div className="flex items-end justify-between">
                                    <span className="text-[10px] font-display tracking-[0.32em] text-white/45">// SUBTOTAL</span>
                                    <span className="font-display text-3xl ember-text">€{subtotal.toFixed(2)}</span>
                                </div>
                                <p className="text-[10px] font-mono text-white/40">+ delivery fee on checkout</p>
                                <motion.button
                                    onClick={() => { onClose?.(); navigate("/checkout"); }}
                                    whileTap={{ scale: 0.98 }}
                                    data-testid="cart-checkout"
                                    className="w-full magnet-cta primary h-14"
                                >
                                    Checkout <ArrowRight size={16} strokeWidth={3} />
                                </motion.button>
                            </footer>
                        )}
                    </motion.aside>
                </>
            )}
        </AnimatePresence>
    );
};
